import { baseApi } from "./baseApi";
import { API_ENDPOINTS } from "../../config/url";

export interface LoyaltyPointItem {
    _id: string;
    name: string;
    type: number;
    points: number;
    minimumSpend: number | null;
    startDate: string;
    endDate: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

export interface Pagination {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface GetLoyaltyPointsResponse {
    data: {
        list: LoyaltyPointItem[];
        pagination: Pagination;
    };
    message: string;
}

export interface GetLoyaltyPointsRequest {
    page?: number;
    limit?: number;
}

export interface CreateLoyaltyPointRequest {
    name: string;
    type: number;
    points: number;
    minimumSpend: number | null;
    startDate: string;
    endDate: string;
}

export interface UpdateLoyaltyPointRequest {
    id: string;
    body: {
        name?: string;
        type?: number;
        points?: number;
        minimumSpend?: number | null;
        startDate?: string;
        endDate?: string;
    }
}

export interface UpdateLoyaltySettingsRequest {
    conversionRate: number;
    minimumPointsForRedemption: number;
    maxRedemptionLimit: number;
}

export interface UpdateLoyaltyPointResponse {
    data: LoyaltyPointItem;
    message: string;
}

export interface CreateLoyaltyPointResponse {
    data: LoyaltyPointItem;
    message: string;
}

export interface DeleteLoyaltyPointRequest {
    id: string;
}

export interface DeleteLoyaltyPointResponse {
    message: string;
    success?: boolean;
}

export interface GetLoyaltyPointDetailsResponse {
    data: LoyaltyPointItem;
    message: string;
}

export const loyaltyAPI = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getLoyaltyPoints: builder.query<GetLoyaltyPointsResponse, GetLoyaltyPointsRequest>({
            query: ({ page = 1, limit = 10 }) => ({
                url: API_ENDPOINTS.LOYALTY.LIST,
                method: "GET",
                params: {
                    page,
                    limit,
                },
                headers: {
                    "x-portal": "admin",
                }
            }),
            providesTags: ["LOYALTY"],
        }),
        createLoyaltyPoint: builder.mutation<CreateLoyaltyPointResponse, CreateLoyaltyPointRequest>({
            query: (body) => ({
                url: API_ENDPOINTS.LOYALTY.CREATE,
                method: "POST",
                body,
                headers: {
                    "x-portal": "admin",
                },
            }),
            invalidatesTags: ["LOYALTY"],
        }),
        deleteLoyaltyPoint: builder.mutation<DeleteLoyaltyPointResponse, DeleteLoyaltyPointRequest>({
            query: ({ id }) => ({
                url: API_ENDPOINTS.LOYALTY.DELETE(id),
                method: "DELETE",
                headers: {
                    "x-portal": "admin",
                },
            }),
            invalidatesTags: ["LOYALTY"],
        }),
        getLoyaltyPointDetails: builder.query<GetLoyaltyPointDetailsResponse, string>({
            query: (id) => ({
                url: API_ENDPOINTS.LOYALTY.UPDATE(id),
                method: "GET",
                headers: {
                    "x-portal": "admin",
                },
            }),
            providesTags: ["LOYALTY"],
        }),
        updateLoyaltyPoint: builder.mutation<UpdateLoyaltyPointResponse, UpdateLoyaltyPointRequest>({
            query: ({ id, body }) => ({
                url: API_ENDPOINTS.LOYALTY.UPDATE(id),
                method: "PUT",
                body,
                headers: {
                    "x-portal": "admin",
                },
            }),
            invalidatesTags: ["LOYALTY"],
        }),
        updateLoyaltySettings: builder.mutation<any, UpdateLoyaltySettingsRequest>({
            query: (body) => ({
                url: API_ENDPOINTS.LOYALTY.SETTINGS_UPDATE,
                method: "PUT",
                body,
                headers: {
                    "x-portal": "admin",
                },
            }),
            invalidatesTags: ["LOYALTY"],
        }),
    }),
});

export const {
    useGetLoyaltyPointsQuery,
    useCreateLoyaltyPointMutation,
    useDeleteLoyaltyPointMutation,
    useGetLoyaltyPointDetailsQuery,
    useUpdateLoyaltyPointMutation,
    useUpdateLoyaltySettingsMutation
} = loyaltyAPI;
